/**
 * @file ProductCostSection.tsx
 * @description Закупочная цена + поставщик (phase08.5 shadcn).
 * Состояние формы и тело запроса — `product-cost-form.ts` (2026-10-19:
 * поставщика можно снять пунктом «Отсутствует»).
 */

import { Wallet } from "lucide-react";
import { useEffect, useState } from "react";

import { DirectoryAutocomplete } from "../../../../components/catalog/DirectoryAutocomplete";
import { SupplierEditDialog } from "../../../../components/catalog/SupplierEditDialog";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import {
  catalogCost,
  catalogSuppliers,
  type CatalogSupplier,
  type ProductCost,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { costFormFrom, costPayload, type CostFormState } from "../product-cost-form";
import { SectionCard } from "./SectionCard";

interface Props {
  productId: string;
}

export function ProductCostSection({ productId }: Props) {
  const notify = useNotify();
  const [cost, setCost] = useState<ProductCost | null>(null);
  const [suppliers, setSuppliers] = useState<CatalogSupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [form, setForm] = useState<CostFormState>(() => costFormFrom(null));

  async function refresh() {
    setLoading(true);
    try {
      const [c, sup] = await Promise.all([
        catalogCost.find(productId),
        catalogSuppliers.list(),
      ]);
      setCost(c);
      setSuppliers(sup);
      setForm(costFormFrom(c));
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function onSave() {
    setSaving(true);
    try {
      // Снятый пунктом «Отсутствует» поставщик уходит как `null` (2026-10-19).
      await catalogCost.upsert(productId, costPayload(form, cost));
      notify.success("Закупочные данные сохранены");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard icon={<Wallet />} title="Закупка и поставщик" collapsible>
      <div className="flex flex-col gap-4">
        <p className="text-xs text-on-surface-variant">
          Видно только под правом SHOP_VIEW_FINANCE. Не публикуется на сайте.
        </p>
        {loading ? (
          <p className="text-xs text-on-surface-muted">Загрузка…</p>
        ) : (
          <>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="cost-price">Закупочная цена</Label>
                <Input
                  id="cost-price"
                  type="number"
                  step={0.01}
                  min={0}
                  value={form.costPrice}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, costPrice: e.target.value }))
                  }
                  disabled={saving}
                />
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="cost-cur">Валюта</Label>
                <Input
                  id="cost-cur"
                  value={form.costCurrency}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      costCurrency: e.target.value.toUpperCase(),
                    }))
                  }
                  maxLength={3}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Поставщик</Label>
              <DirectoryAutocomplete
                items={suppliers}
                value={form.supplierId}
                onChange={(id) => setForm((f) => ({ ...f, supplierId: id }))}
                onCreateClick={() => setCreatingSupplier(true)}
                disabled={saving}
                renderSecondary={(s) =>
                  [
                    s.inn ? `ИНН ${s.inn}` : null,
                    s.defaultDeliveryDays
                      ? `Доставка ${s.defaultDeliveryDays} дн.`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
              />
              <p className="text-xs text-on-surface-variant">
                Можно создать нового поставщика inline
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cost-code">Внутренний артикул</Label>
              <Input
                id="cost-code"
                value={form.internalCode}
                onChange={(e) =>
                  setForm((f) => ({ ...f, internalCode: e.target.value }))
                }
                disabled={saving}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cost-notes">Заметка финансиста</Label>
              <Textarea
                id="cost-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                disabled={saving}
                rows={2}
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              {cost ? (
                <span className="text-xs text-on-surface-variant">
                  Обновлено: {new Date(cost.updatedAt).toLocaleString("ru-RU")}
                </span>
              ) : null}
              <Button onClick={() => void onSave()} disabled={saving}>
                {saving ? "…" : "Сохранить закупку"}
              </Button>
            </div>
          </>
        )}
      </div>

      {creatingSupplier ? (
        <SupplierEditDialog
          open
          supplier={null}
          onClose={() => setCreatingSupplier(false)}
          onSaved={async (saved) => {
            setCreatingSupplier(false);
            await refresh();
            setForm((f) => ({ ...f, supplierId: saved.id }));
          }}
        />
      ) : null}
    </SectionCard>
  );
}
